import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '@/features/settings/api';
import { keys } from '@/lib/query/keys';
import type { Locale } from '@/lib/api/types';

/**
 * Coerce a capabilities `brand.locales` value into the console's Locale union.
 *
 * Absent (capabilities not loaded yet, or an old server) ⇒ English only, which is what
 * every deployment serves. Guessing wider would put a control on screen that the server
 * refuses, and — for the list chrome this also drives — would advertise a translation
 * workflow the deployment does not have.
 */
export function asLocaleList(v: unknown): Locale[] {
  if (!Array.isArray(v) || v.length === 0) return ['en'];
  const locales = v.filter((x): x is Locale => x === 'en' || x === 'ar');
  return locales.length > 0 ? locales : ['en'];
}

/**
 * The deployment's configured locale set, read from `GET /admin/capabilities` (which the
 * whole admin group already grants at `content:read`, so an Editor gets it too).
 *
 * Two different surfaces need this and neither may hardcode it:
 *  - the settings form, which must not offer a locale the server will reject on save;
 *  - every list and editor's EN/AR chrome — an "AR" column and a "Missing AR" filter on a
 *    single-locale deployment are permanently amber columns for a translation that is not
 *    coming, which is the inherited chrome PHASE 04 removes.
 *
 * `single` is the thing callers actually branch on, so the check reads the same everywhere
 * rather than being re-derived as `locales.length === 1` in each screen.
 *
 * `showArChrome` is the one every EN/AR affordance should branch on: Arabic chrome earns its
 * place when the deployment serves Arabic, and also when the publish gate demands an
 * Arabic-complete flag — hiding the switch under a live gate would leave nobody able to
 * publish.
 */
export function useServerLocales(): {
  locales: Locale[];
  single: boolean;
  isBilingual: boolean;
  requireArComplete: boolean;
  showArChrome: boolean;
} {
  const caps = useQuery({
    queryKey: keys.settings.capabilities(),
    queryFn: () => settingsApi.getCapabilities(),
    // The locale set changes with a deployment, not with a request; a stale-time this long
    // means one fetch paints every screen in a session instead of one per screen.
    staleTime: 5 * 60_000,
  });
  const locales = asLocaleList(caps.data?.brand.locales);
  const isBilingual = locales.length > 1;
  const requireArComplete = caps.data?.publish_gate.require_ar_complete === true;
  return {
    locales,
    single: !isBilingual,
    isBilingual,
    requireArComplete,
    showArChrome: isBilingual || requireArComplete,
  };
}
